/* eslint-disable no-template-curly-in-string */
import * as yup from "yup";
import { CampaignContact } from "../../App/Enums/CampaignTypes";
import { ICampaignFormField } from "../../App/Typings/Campaigns";

export const maxFieldLabelLength = 400;
export const maxCampaignNameLength = 50;
export const maxWelcomeSubjectLength = 100;
export const maxWelcomeEmailLength = 1500;
export const maxWelcomeEmailLengthDisplay = maxWelcomeEmailLength.toLocaleString();
export const maxWelcomeTextLength = 600;
export const maxWelcomeTextSmsLength = 160;
export const minTextSegmentThreshold = 35; // Minimum length to check if an SMS message will be segmented
export const getMaxMessageLength = (isEmail: boolean) => {
  return isEmail ? maxWelcomeEmailLength : maxWelcomeTextLength;
};
export const getMaxMessageError = (isEmail: boolean) => {
  return `Welcome Message cannot be longer than ${
    isEmail ? maxWelcomeEmailLengthDisplay : maxWelcomeTextLength
  } characters`;
};
export const maxWelcomeSubjectError = `Welcome Subject cannot be longer than ${maxWelcomeSubjectLength} characters`;
export const minWelcomeMessageLengthError = "Welcome Message cannot be empty";
export const minWelcomeSubjectLengthError = "Welcome Subject cannot be empty";

export const FieldModalSchema = yup.object().shape({
  fieldType: yup.string().required("Field Type is required"),
  fieldLabel: yup
    .string()
    .required("Field Label is required")
    .max(maxFieldLabelLength, `Field Label cannot exceed ${maxFieldLabelLength} characters`)
    .test(
      "label-is-unique",
      "The Field Label must be unique, please check both active and archived fields.",
      (value, context: any) => {
        const existingLabels = Object.values(context.options.context.fields as ICampaignFormField[]).map(
          (v: ICampaignFormField) => v.label.toLowerCase(),
        );
        const form = context.options.context;
        const originalLabel = form?.fields[form?.editedField]?.label.toLowerCase().trim() || "";
        const originalValue = context.originalValue.toLowerCase().trim();

        return originalValue === originalLabel || !existingLabels.includes(originalValue);
      },
    ),
  maxLength: yup.number().when("fieldType", {
    is: "text",
    then: yup
      .number()
      .moreThan(0, "Text Length must be greater than 0")
      .max(10000, "Text Length must be lower than 10000")
      .typeError("Text Length must be a number")
      .test("no-leading-zeroes", "Value cannot start with 0", (value, context: any) => {
        return (
          (context.originalValue && context.originalValue === "0") || !context.originalValue.startsWith("0")
        );
      }),
  }),
  dropdownOptions: yup.array().when("fieldType", {
    is: "dropdown",
    then: yup.array().of(yup.string()).min(1, "There must be at least 1 dropdown option"),
  }),
});

export type CampaignSchemaType = {
  startDate: Date;
  endDate?: Date;
  ongoing?: boolean | undefined;
  contactPreference: string;
  campaignName: string;
  welcomeMessage: string;
  welcomeMessageSubject?: string | null;
};

export const CampaignSchema: yup.SchemaOf<CampaignSchemaType> = yup.object().shape({
  startDate: yup.date().required("Start Date is Required").typeError("Please enter a valid date"),
  endDate: yup
    .date()
    .typeError("Please enter a valid date")
    .when("ongoing", {
      is: false,
      then: yup.date().required("End Date is required").typeError("Please enter a valid date"),
      otherwise: yup.date().nullable().typeError("Please enter a valid date"),
    })
    .when(["startDate", "ongoing"], {
      is: (startDate: any, ongoing: any) => !ongoing && !!startDate,
      then: yup
        .date()
        .typeError("Please enter a valid date")
        .test(
          "end-date-before-start",
          "End Date cannot be before or on Start Date",
          (value, context: any) => {
            return !!value && value > context.parent.startDate;
          },
        ),
    }),
  ongoing: yup.boolean(),
  contactPreference: yup
    .string()
    .required("Contact Preference is Required")
    .typeError("Please select a valid Candidate Contact Preference"),
  campaignName: yup
    .string()
    .required("Form Name is Required")
    .min(1, "Form Name cannot be empty")
    .max(maxCampaignNameLength, "Form Name cannot be longer than ${max} characters"),
  welcomeMessage: yup
    .string()
    .required("Welcome Message is Required")
    .min(1, minWelcomeMessageLengthError)
    .when("contactPreference", {
      is: (contactPreference: string) => contactPreference.includes(CampaignContact.Email),
      // CRM-485 Remove max length validation for email welcome message until Large Email Templates are implemented
      // then: yup
      //   .string()
      //   .max(maxWelcomeEmailLength, "Welcome Message cannot be longer than ${max} characters"),
      otherwise: yup
        .string()
        .max(maxWelcomeTextLength, "Welcome Message cannot be longer than ${max} characters"),
    }),
  welcomeMessageSubject: yup
    .string()
    .nullable(true)
    .min(1, minWelcomeSubjectLengthError)
    .max(maxWelcomeSubjectLength, "Welcome Subject cannot be longer than ${max} characters")
    .when("contactPreference", {
      is: (contactPreference: string) => contactPreference.includes(CampaignContact.Email),
      then: yup
        .string()
        .nullable(true)
        .required("Welcome Subject is required when Email contact preference is selected"),
    }),
});
